import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getAPIUrl } from "../api/apiUtils";
import { apiFetch } from "../api/apiClient";

const POLL_INTERVAL_MS = 10000;
const MAX_SEEN_IDS = 500;

const storageKey = (userId) => `taskNotificationsSeen:${userId}`;

const loadSeenIds = (userId) => {
  if (!userId) return new Set();
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr.map(String));
  } catch {
    return new Set();
  }
};

const saveSeenIds = (userId, seenSet) => {
  if (!userId) return;
  try {
    const arr = Array.from(seenSet).slice(-MAX_SEEN_IDS);
    localStorage.setItem(storageKey(userId), JSON.stringify(arr));
  } catch {
    // ignore
  }
};

const notificationEventKey = (item) => {
  const payload = item?.payload || {};
  return [
    item?.task_id || "",
    item?.session_id || "",
    item?.type || "",
    item?.message || "",
    payload.kind || "",
    payload.stage || "",
    payload.status || "",
    payload.parser_type || "",
    payload.report_id || "",
    payload.action || "",
    payload.source || "",
    payload.community_id || "",
  ].map((part) => String(part)).join("|");
};

const notificationRenderKey = (item) => {
  const payload = item?.payload || {};
  return [
    item?.notification_id || "",
    item?.read === false ? "unread" : "read",
    item?.read_at || "",
    item?.created_at || "",
    item?.updated_at || "",
    item?.type || "",
    item?.message || "",
    item?.task_id || "",
    item?.session_id || "",
    payload.status || "",
    payload.stage || "",
  ].map((part) => String(part)).join("|");
};

const sameNotifications = (left, right) => {
  if (left.length !== right.length) return false;
  return left.every((item, index) => notificationRenderKey(item) === notificationRenderKey(right[index]));
};

export const useTaskNotifications = (user) => {
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pollError, setPollError] = useState(null);
  const [seenIds, setSeenIds] = useState(() => new Set());
  const [seenLoaded, setSeenLoaded] = useState(false);
  const lastPollAtRef = useRef(null);
  const hasLoadedRef = useRef(false);

  const userId = useMemo(() => user?.id ?? user?.id_user, [user]);

  useEffect(() => {
    if (!userId) {
      setSeenIds(new Set());
      setSeenLoaded(false);
      setNotifications([]);
      lastPollAtRef.current = null;
      hasLoadedRef.current = false;
      setIsLoading(false);
      return;
    }
    setSeenIds(loadSeenIds(userId));
    setSeenLoaded(true);
    setNotifications([]);
    lastPollAtRef.current = null;
    hasLoadedRef.current = false;
    setIsLoading(false);
  }, [userId]);

  const markSeen = useCallback(
    (ids) => {
      if (!userId) return;
      const list = Array.isArray(ids) ? ids : [ids];
      if (list.length === 0) return;

      setSeenIds((prev) => {
        const next = new Set(prev);
        list.forEach((id) => {
          if (id !== undefined && id !== null) next.add(String(id));
        });
        saveSeenIds(userId, next);
        return next;
      });
    },
    [userId]
  );

  const markRead = useCallback(
    async (ids) => {
      if (!userId) return;
      const list = (Array.isArray(ids) ? ids : [ids]).filter(Boolean).map(String);
      if (list.length === 0) return;

      markSeen(list);
      setNotifications((prev) =>
        prev.map((item) =>
          list.includes(String(item?.notification_id))
            ? { ...item, read: true, read_at: item.read_at || new Date().toISOString() }
            : item
        )
      );

      await Promise.all(
        list.map(async (notificationId) => {
          try {
            await apiFetch(`${getAPIUrl()}/tasks/notifications/${encodeURIComponent(notificationId)}/read`, {
              method: "PATCH",
            });
          } catch (error) {
            console.error("Ошибка отметки уведомления:", error);
          }
        })
      );
    },
    [markSeen, userId]
  );

  useEffect(() => {
    if (!userId || !seenLoaded) {
      setNotifications([]);
      return undefined;
    }

    let isMounted = true;
    let pollTimer = null;
    let abortController = null;

    const pollNotifications = async () => {
      if (abortController) abortController.abort();
      abortController = new AbortController();
      const isInitialPoll = !hasLoadedRef.current;

      try {
        if (isInitialPoll && isMounted) setIsLoading(true);
        const params = new URLSearchParams({
          user_id: String(userId),
          limit: "100",
        });
        const response = await apiFetch(`${getAPIUrl()}/tasks/notifications?${params.toString()}`, {
          method: "GET",
          cache: "no-store",
          signal: abortController.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data = await response.json();
        const list = Array.isArray(data?.notifications) ? data.notifications : [];
        const relevant = list.filter((item) => {
          if (item?.user === undefined || item?.user === null) return true;
          return String(item.user) === String(userId);
        });

        relevant.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

        if (isMounted) {
          setNotifications((prev) => {
            const byId = new Map(prev.map((item) => [String(item.notification_id), item]));
            relevant.forEach((item) => {
              if (item?.notification_id) byId.set(String(item.notification_id), item);
            });
            const byEvent = new Map();
            Array.from(byId.values())
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
              .forEach((item) => {
                const key = notificationEventKey(item);
                if (!byEvent.has(key)) {
                  byEvent.set(key, item);
                }
              });

            const next = Array.from(byEvent.values()).slice(0, 100);
            return sameNotifications(prev, next) ? prev : next;
          });
          hasLoadedRef.current = true;
          if (data?.polled_at) {
            lastPollAtRef.current = data.polled_at;
          } else {
            lastPollAtRef.current = new Date().toISOString();
          }
          setPollError(null);
        }
      } catch (error) {
        if (error?.name === "AbortError") return;
        if (isMounted) {
          hasLoadedRef.current = true;
          setPollError("Не удалось обновить уведомления");
        }
      } finally {
        if (isInitialPoll && isMounted) setIsLoading(false);
      }
    };

    pollNotifications();
    pollTimer = window.setInterval(pollNotifications, POLL_INTERVAL_MS);

    return () => {
      isMounted = false;
      if (pollTimer) window.clearInterval(pollTimer);
      if (abortController) abortController.abort();
    };
  }, [userId, seenLoaded]);

  const unreadCount = useMemo(() => {
    return notifications.filter((n) => n?.read === false).length;
  }, [notifications]);

  const markAllSeen = useCallback(() => {
    markSeen(
      notifications
        .map((n) => n?.notification_id)
        .filter(Boolean)
        .map(String)
    );
  }, [notifications, markSeen]);

  const markAllRead = useCallback(() => {
    markRead(
      notifications
        .map((n) => n?.notification_id)
        .filter(Boolean)
        .map(String)
    );
  }, [notifications, markRead]);

  return {
    notifications,
    unreadCount,
    isLoading,
    pollError,
    markSeen,
    markRead,
    markAllSeen,
    markAllRead,
    seenIds,
    seenLoaded,
  };
};
